/**
 * E10 on the owner's side. A folder the user shares, or any folder above it, can be renamed or
 * moved within My Files, but not through the storage API: S3 has no rename, and the share has to
 * follow the folder to its new path. Those go to `POST /v1/shares/moves` (`./shareMoves`), after
 * the user confirms; everything else keeps using `/storage/move` and `/storage/rename`.
 *
 * Two things stay refused: moving such a folder into a folder someone else shares (shared folders
 * don't nest), and, from the storage API, any rename or move of it (409 `shared_folder_locked`).
 */
import {readShareError, safeServerMessage} from './errors'
import {shareFolderDisplayName} from './shareEligibility'
import type {ShareView} from './types'

/**
 * The share that `path` (an item in My Files: a file, or a folder ending in `/`) carries when it is
 * renamed or moved: the shared folder itself, or a shared folder inside it. Files carry none.
 */
export function shareLockingPath(path: string, sharedByMe: ShareView[]): ShareView | undefined {
  if (!path.endsWith('/')) {
    return undefined
  }
  return sharedByMe.find((share) => share.folderPath.startsWith(path))
}

/**
 * The routing decision for a rename or move in My Files: true when any of `paths` is a shared
 * folder or a folder above one, so the whole selection goes to `POST /v1/shares/moves` (with a
 * confirmation) rather than to the storage API, which would refuse it.
 */
export function movesSharedFolder(paths: string[], sharedByMe: ShareView[]): boolean {
  return paths.some((path) => !!shareLockingPath(path, sharedByMe))
}

/** Why `path` can't be moved into a folder someone else shares, for the Move/Copy dialog. */
export function lockedFolderMessage(path: string, share: ShareView): string {
  const name = share.name || shareFolderDisplayName(share.folderPath)
  return share.folderPath === path
    ? `“${name}” is shared, and a shared folder can't be moved into another shared folder.`
    : `It contains the shared folder “${name}”, and a shared folder can't be moved into another shared folder.`
}

/**
 * The first item of `paths` that can't be moved into someone else's shared folder, with the
 * reason; undefined if none.
 */
export function firstLockedPath(
  paths: string[],
  sharedByMe: ShareView[]
): {path: string; share: ShareView; message: string} | undefined {
  for (const path of paths) {
    const share = shareLockingPath(path, sharedByMe)
    if (share) {
      return {path, share, message: lockedFolderMessage(path, share)}
    }
  }
  return undefined
}

const LOCKED_FALLBACK =
  'This folder is shared, or contains a shared folder. Refresh My Files and try again.'

/** The storage API refused a rename or move because it involves a shared folder (E10). */
export function isSharedFolderLocked(error: unknown): boolean {
  const {status, code} = readShareError(error)
  return status === 409 && code === 'shared_folder_locked'
}

/**
 * The text for a `/storage/rename` or `/storage/move` refused by E10 (409 `shared_folder_locked`,
 * `{code, folderPath, message}`), or undefined for any other error. The server's message names the
 * folder; it is shown once {@link safeServerMessage} has checked it. Only a fallback: the Files
 * browser sends such moves to the sharing API instead (when its list of shares was out of date, on
 * this very refusal).
 */
export function sharedFolderLockedMessage(error: unknown): string | undefined {
  if (!isSharedFolderLocked(error)) {
    return undefined
  }
  return safeServerMessage(readShareError(error).serverMessage) ?? LOCKED_FALLBACK
}
